import { Brain, HeartHandshake, ShieldCheck, MessageCircle, Users, LifeBuoy, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

export const therapies = [
  {
    icon: Brain,
    title: "Cognitive Behavioral Therapy (CBT)",
    desc: "Identify the thoughts and situations that drive drinking or drug use, and build practical ways to respond differently. CBT is the foundation of both individual and group sessions here.",
    usedIn: "PHP · IOP · Virtual IOP",
    linkText: "How CBT works in addiction treatment",
    href: "/therapies-cherry-hill-nj#cbt",
  },
  {
    icon: HeartHandshake,
    title: "Dialectical Behavior Therapy (DBT)",
    desc: "Skills for managing intense emotions, tolerating distress and improving relationships without turning to substances. Especially helpful when anxiety, depression or mood swings sit underneath the addiction.",
    usedIn: "PHP · IOP · Virtual IOP",
    linkText: "DBT skills for emotional regulation",
    href: "/therapies-cherry-hill-nj#dbt",
  },
  {
    icon: ShieldCheck,
    title: "Trauma-Informed Care",
    desc: "Many people who struggle with substances are also carrying trauma. Our therapists are trained to recognize it, keep sessions safe and paced, and address trauma without forcing you to relive it.",
    usedIn: "PHP · IOP · Virtual IOP",
    linkText: "Trauma-informed addiction therapy",
    href: "/therapies-cherry-hill-nj#trauma-informed-care",
  },
  {
    icon: MessageCircle,
    title: "Motivational Interviewing",
    desc: "A collaborative, non-judgmental approach that helps you work through mixed feelings about change and find your own reasons to stay in treatment. Often the starting point for people who arrive unsure.",
    usedIn: "PHP · IOP · Virtual IOP",
    linkText: "Motivational interviewing for recovery",
    href: "/therapies-cherry-hill-nj#motivational-interviewing",
  },
  {
    icon: Users,
    title: "Family Therapy",
    desc: "Structured sessions that bring family members into the process, rebuild trust and set healthy boundaries. Because recovery is stronger when the people around you understand it.",
    usedIn: "PHP · IOP",
    linkText: "Family therapy for addiction recovery",
    href: "/therapies-cherry-hill-nj#family-therapy",
  },
  {
    icon: LifeBuoy,
    title: "Relapse Prevention",
    desc: "Mindfulness-based skills for recognizing cravings, handling high-risk situations and planning for life after treatment. Every patient leaves with a written relapse-prevention plan.",
    usedIn: "PHP · IOP · Virtual IOP",
    linkText: "Relapse prevention planning after treatment",
    href: "/therapies-cherry-hill-nj#relapse-prevention",
  },
];

const Therapies = () => (
  <section id="therapies" className="py-20 md:py-24 bg-background">
    <div className="container">
      <div className="max-w-3xl mx-auto text-center mb-14">
        <p className="text-secondary font-semibold text-sm tracking-wider uppercase mb-3">How We Treat</p>
        <h2 className="font-display text-3xl md:text-5xl font-bold text-primary text-balance">
          Evidence-Based Therapies We Use in Cherry Hill
        </h2>
        <p className="mt-5 text-lg text-muted-foreground">
          Every patient in PHP, IOP or Virtual IOP works with licensed therapists using proven, evidence-based
          approaches. Treatment is individualized, and co-occurring anxiety, depression and trauma are treated
          alongside substance use under the oversight of our board-certified psychiatrist.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {therapies.map(({ icon: Icon, title, desc, usedIn, linkText, href }) => (
          <article
            key={title}
            className="group relative bg-card border border-border rounded-2xl p-8 shadow-soft hover:shadow-elegant transition-all duration-500 hover:-translate-y-1 flex flex-col"
          >
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-xl bg-secondary/10 text-secondary mb-6 group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors">
              <Icon className="h-7 w-7" />
            </div>
            <h3 className="font-display text-xl md:text-2xl font-semibold text-primary mb-3">{title}</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">{desc}</p>
            <p className="text-sm text-muted-foreground mb-6">
              <span className="font-semibold text-primary">Used in:</span> {usedIn}
            </p>
            <a
              href={href}
              aria-label={`${linkText} at Cherry Hill Recovery Center`}
              className="mt-auto inline-flex items-center gap-2 text-secondary font-semibold hover:gap-3 transition-all"
            >
              {linkText} <ArrowRight className="h-4 w-4" />
            </a>
          </article>
        ))}
      </div>

      <p className="mt-12 text-center text-lg text-muted-foreground max-w-3xl mx-auto">
        Not sure which approach is right for you? Our clinical team matches therapies to your history and goals
        during your first assessment.
      </p>
      <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
        <Button asChild size="lg" className="rounded-full px-8 h-12 font-semibold">
          <a href="/therapies-cherry-hill-nj">See all therapies offered in Cherry Hill</a>
        </Button>
        <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full px-8 h-12 font-semibold">
          <a href="tel:8562003127"><Phone className="h-4 w-4" /> Call 856-200-3127</a>
        </Button>
      </div>
    </div>
  </section>
);

export default Therapies;
